// Payment panel — right-column inline state machine: summary → cash → qr.
// Replaces PaymentSummary + PaymentCashDialog + PaymentQRDialog overlays.
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PaymentCashSection } from "./payment-cash-section";
import { PaymentQRSection } from "./payment-qr-section";
import { CustomerSelect } from "./customer-select";
import type { Order } from "./use-pos-cart";

export type PaymentView = "summary" | "cash" | "qr";

function fmtVND(n: number) {
  return new Intl.NumberFormat("vi-VN").format(Math.round(n)) + " ₫";
}

export interface PaymentPanelProps {
  order: Order | null;
  coupon: { code: string; amountOff: number } | null;
  onDiscount: (amount: string) => void;
  onPayCash: (tenderedAmount: number, couponCode?: string, reason?: string) => void;
  onPayQR: () => void; // reserved for API compat
  onQRSuccess: () => void;
  onNewOrder: () => void;
  loading?: boolean;
  view: PaymentView;
  onViewChange: (view: PaymentView) => void;
  // Customer selector is rendered at the top of the summary view only when
  // onSelectCustomer is provided. Desktop wires it here; mobile keeps its own
  // toolbar chip and omits these so the selector is not shown twice.
  selectedCustomerId?: string | null;
  onSelectCustomer?: (customer: { id: string } | null) => void;
}

export function PaymentPanel({
  order,
  coupon,
  onDiscount,
  onPayCash,
  onQRSuccess,
  onNewOrder,
  loading,
  view,
  onViewChange,
  selectedCustomerId,
  onSelectCustomer,
}: PaymentPanelProps) {
  const subtotal = parseFloat(order?.subtotal ?? "0");
  const discountAmount = parseFloat(order?.discountAmount ?? "0");
  const couponAmount = coupon?.amountOff ?? 0;
  const total = Math.max(0, subtotal - discountAmount - couponAmount);
  const hasCustomer = !!order?.customerId;
  const canPay = !!order && total > 0 && order.status !== "paid";

  const handleDiscount = (e: React.ChangeEvent<HTMLInputElement>) => {
    onDiscount(e.target.value || "0");
  };

  const handleCashConfirm = (tenderedAmount: number, couponCode?: string, reason?: string) => {
    onPayCash(tenderedAmount, couponCode, reason);
    // view reset happens in pos-screen-page after onSuccess
  };

  const handleQRSuccess = () => {
    onQRSuccess();
    onViewChange("summary");
  };

  // QR back: just return to summary (cancel is handled inside PaymentQRSection).
  const handleBack = () => onViewChange("summary");

  if (view === "cash") {
    return (
      <PaymentCashSection
        total={total}
        hasCustomer={hasCustomer}
        couponCode={coupon?.code}
        onConfirm={handleCashConfirm}
        onBack={handleBack}
        loading={loading}
      />
    );
  }

  if (view === "qr" && order) {
    return (
      <PaymentQRSection
        orderId={order.id}
        onSuccess={handleQRSuccess}
        onBack={handleBack}
      />
    );
  }

  // Default: summary view.
  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Customer — grouped with the checkout step for quick access */}
      {onSelectCustomer && (
        <CustomerSelect
          selectedId={selectedCustomerId}
          onSelect={onSelectCustomer}
          mode="inline"
        />
      )}

      {/* Totals */}
      <div className="space-y-2 text-sm">
        <div className="flex justify-between text-muted-foreground">
          <span>Tạm tính</span>
          <span>{fmtVND(subtotal)}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground shrink-0">Giảm giá đơn</span>
          <Input
            type="number"
            min={0}
            defaultValue={order?.discountAmount ?? "0"}
            onBlur={handleDiscount}
            onKeyDown={(e) => e.key === "Enter" && (e.currentTarget as HTMLInputElement).blur()}
            className="w-36 text-right font-mono text-sm"
            disabled={!order}
          />
        </div>
        {couponAmount > 0 && (
          <div className="flex justify-between text-green-600">
            <span>Coupon</span>
            <span>-{fmtVND(couponAmount)}</span>
          </div>
        )}
        <div className="flex justify-between border-t pt-2 text-lg font-bold">
          <span>Tổng cộng</span>
          <span className="text-primary">{fmtVND(total)}</span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="space-y-2">
        <Button
          className="w-full text-base font-semibold"
          disabled={!canPay || loading}
          onClick={() => onViewChange("cash")}
        >
          Tiền mặt (F9)
        </Button>
        <Button
          variant="outline"
          className="w-full font-semibold"
          disabled={!canPay || loading}
          onClick={() => onViewChange("qr")}
        >
          Chuyển khoản QR (F8)
        </Button>
        <Button
          variant="outline"
          className="w-full"
          disabled={loading}
          onClick={onNewOrder}
        >
          Đơn mới (F11)
        </Button>
      </div>

      {/* Keyboard hint bar */}
      <div className="mt-2 rounded-md bg-muted p-3 text-xs text-muted-foreground space-y-1">
        <div><kbd className="rounded border px-1">F2</kbd> Tìm sản phẩm</div>
        <div><kbd className="rounded border px-1">F8</kbd> Thanh toán QR</div>
        <div><kbd className="rounded border px-1">F9</kbd> Thanh toán tiền mặt</div>
        <div><kbd className="rounded border px-1">F11</kbd> Đơn hàng mới</div>
      </div>
    </div>
  );
}
